import { useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { CopyPlusIcon, Trash2Icon } from 'lucide-react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import FormInputWrapper from 'components/input/FormInputWrapper';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import {
  AUTO_VEHICLE_TYPE_OPTIONS,
  canonicalAutoVehicleType,
} from 'lib/autoTransport/autoVehicleTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { useSuperDispatchVehicleCatalog } from '../../hooks/useSuperDispatchVehicleCatalog';
import { QuickQuoteInputs } from '../../types';
import {
  autoTransportMakeError,
  autoTransportModelError,
  autoTransportTypeError,
  autoTransportYearError,
} from '../../utils/validation';
import {
  getModelNamesForMake,
  inferVehicleTypeForMakeModel,
} from '../superDispatchVehicleCatalog';
import { AutoTransportFieldError } from './AutoTransportFieldError';
import { SegmentedToggle, SegmentedToggleOption } from './SegmentedToggle';

enum AutoVehicleCondition {
  Operable = 'operable',
  Inoperable = 'inoperable',
}

const CONDITION_OPTIONS: readonly [
  SegmentedToggleOption<AutoVehicleCondition>,
  SegmentedToggleOption<AutoVehicleCondition>,
] = [
  {
    value: AutoVehicleCondition.Operable,
    label: 'Operable',
    ariaLabel: 'Operable vehicle',
  },
  {
    value: AutoVehicleCondition.Inoperable,
    label: 'INOP',
    ariaLabel: 'Inoperable vehicle',
  },
];

const MAKE_SELECT_TRIGGER_CLASSNAME = 'w-full';

const STACKED_SELECT_TRIGGER_CLASSNAME = 'mt-1 w-full';

// Stable hooks for harness / Playwright (see tests/e2e/helpers/autoTransportForm.ts).
function autoVehicleFieldTestId(
  index: number,
  field: 'year' | 'make' | 'model' | 'type'
) {
  return `quick-quote-auto-vehicle-${index}-${field}`;
}

type AutoVehicleFieldsProps = {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  index: number;
  vehicleCount: number;
  canRemove: boolean;
  onRemove: () => void;
  canDuplicate: boolean;
  onDuplicate: () => void;
};

export function AutoVehicleFields({
  formMethods,
  index,
  vehicleCount,
  canRemove,
  onRemove,
  canDuplicate,
  onDuplicate,
}: AutoVehicleFieldsProps) {
  const {
    control,
    clearErrors,
    formState: { errors },
    setValue,
  } = formMethods;
  const vehiclePath = `autoTransport.vehicles.${index}` as const;
  const { isReady: isCatalogReady, makes } = useSuperDispatchVehicleCatalog();
  const selectedMake = useWatch({ control, name: `${vehiclePath}.make` }) ?? '';
  const selectedModel =
    useWatch({ control, name: `${vehiclePath}.model` }) ?? '';

  const makeSelectOptions = useMemo(
    () => toSelectOptions(makes, selectedMake),
    [makes, selectedMake]
  );

  const modelNames = useMemo(
    () => (isCatalogReady ? getModelNamesForMake(selectedMake) : []),
    [isCatalogReady, selectedMake]
  );

  const modelSelectOptions = useMemo(
    () => toSelectOptions(modelNames, selectedModel),
    [modelNames, selectedModel]
  );

  // Show the catalog's spelling as selected without rewriting form state.
  const makeSelectValue =
    matchCatalogValue(makes, selectedMake) ?? selectedMake;
  const modelSelectValue =
    matchCatalogValue(modelNames, selectedModel) ?? selectedModel;

  return (
    <Flex
      direction='col'
      gap='sm'
      className='w-full min-w-0 rounded-[4px] border border-neutral-400/70 bg-neutral-50 p-3'
    >
      <Flex
        justify='between'
        align='center'
        gap='sm'
        className='w-full min-w-0'
      >
        <Typography variant='body-sm' weight='semibold'>
          {vehicleCount > 1 ? `Vehicle ${index + 1}` : 'Vehicle'}
        </Typography>
        <Flex align='center' gap='xs' className='shrink-0'>
          {canDuplicate && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-6 px-1.5 text-xs'
              buttonNamePosthog={
                ButtonNamePosthog.QuickQuoteAutoDuplicateVehicle
              }
              onClick={onDuplicate}
              aria-label={`Duplicate vehicle ${index + 1}`}
              data-testid={`quick-quote-auto-vehicle-${index}-duplicate`}
            >
              <CopyPlusIcon className='mr-1 h-3.5 w-3.5' aria-hidden />
              Duplicate
            </Button>
          )}
          {canRemove && (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='h-6 w-6'
              buttonNamePosthog={ButtonNamePosthog.QuickQuoteAutoRemoveVehicle}
              onClick={onRemove}
              aria-label={`Remove vehicle ${index + 1}`}
            >
              <Trash2Icon className='h-3.5 w-3.5' aria-hidden />
            </Button>
          )}
        </Flex>
      </Flex>

      <Grid cols='2' gap='md' align='start' className='min-w-0'>
        <RHFTextInput
          name={`${vehiclePath}.year`}
          label='Year'
          inputType='text'
          placeholder='e.g. 2020'
          inputClassName='placeholder:text-xs'
          maxLength={4}
          required
          data-testid={autoVehicleFieldTestId(index, 'year')}
          options={{
            validate: (value: string) => autoTransportYearError(value) ?? true,
          }}
        />
        <Controller
          name={`${vehiclePath}.make`}
          control={control}
          rules={{
            validate: (value) => autoTransportMakeError(value ?? '') ?? true,
          }}
          render={({ field: makeField }) => (
            <FormInputWrapper
              name={`${vehiclePath}.make`}
              label='Make'
              required
            >
              <Select
                options={makeSelectOptions}
                showSearch
                preferSelectedHighlight
                disabled={!isCatalogReady}
                value={makeSelectValue || undefined}
                onChange={(value) => {
                  const nextMake = String(value ?? '');
                  // Reps can't pick an empty option. An empty value comes from the
                  // form's native select while the shown value moves from the
                  // suggestion's spelling ("Ram") to the catalog's ("RAM"), and
                  // must not wipe the suggested make, model and type.
                  if (!nextMake || nextMake === makeSelectValue) {
                    return;
                  }

                  makeField.onChange(nextMake);
                  // Clear the dependent values and any errors from an earlier
                  // submit. Validation runs after the rep makes another choice.
                  setValue(`${vehiclePath}.model`, '', { shouldDirty: true });
                  setValue(`${vehiclePath}.type`, '', { shouldDirty: true });
                  clearErrors([`${vehiclePath}.model`, `${vehiclePath}.type`]);
                }}
              >
                <SelectTrigger
                  className={MAKE_SELECT_TRIGGER_CLASSNAME}
                  data-testid={autoVehicleFieldTestId(index, 'make')}
                >
                  <SelectValue placeholder='Search' />
                </SelectTrigger>
                <SelectContent />
              </Select>
            </FormInputWrapper>
          )}
        />
      </Grid>

      <Flex direction='col' gap='sm' className='w-full min-w-0'>
        <Controller
          name={`${vehiclePath}.model`}
          control={control}
          rules={{
            validate: (value) =>
              autoTransportModelError(selectedMake, value ?? '') ?? true,
          }}
          render={({ field: modelField }) => (
            <Flex direction='col' gap='xs' className='w-full min-w-0'>
              <Label name={`${vehiclePath}.model`} required>
                Model
              </Label>
              <Select
                options={modelSelectOptions}
                showSearch
                preferSelectedHighlight
                disabled={selectedMake === ''}
                value={modelSelectValue || undefined}
                onChange={(value) => {
                  const model = String(value ?? '');
                  // Same as make: ignore the native select's empty value.
                  if (!model || model === modelSelectValue) {
                    return;
                  }

                  modelField.onChange(model);

                  // Fill the catalog's body type so the rep can review or
                  // change it before submitting.
                  const inferredType = inferVehicleTypeForMakeModel(
                    selectedMake,
                    model
                  );
                  // Don't validate an empty reset. It would show the required
                  // error before the rep has a chance to choose a type.
                  setValue(`${vehiclePath}.type`, inferredType ?? '', {
                    shouldValidate: Boolean(inferredType),
                    shouldDirty: true,
                  });
                }}
              >
                <SelectTrigger
                  className={STACKED_SELECT_TRIGGER_CLASSNAME}
                  data-testid={autoVehicleFieldTestId(index, 'model')}
                >
                  <SelectValue placeholder='Search' />
                </SelectTrigger>
                <SelectContent />
              </Select>
              <AutoTransportFieldError
                errors={errors}
                name={`${vehiclePath}.model`}
              />
            </Flex>
          )}
        />

        <Flex direction='col' gap='xs' className='w-full min-w-0'>
          <Label name={`${vehiclePath}.type`} required>
            Type
          </Label>
          <Controller
            name={`${vehiclePath}.type`}
            control={control}
            rules={{
              validate: (value) => autoTransportTypeError(value ?? '') ?? true,
            }}
            render={({ field: vehicleTypeField }) => (
              <Select
                onValueChange={vehicleTypeField.onChange}
                // Empty string keeps the Select controlled so an inferred type
                // shows up instead of the placeholder.
                value={
                  canonicalAutoVehicleType(vehicleTypeField.value ?? '') ?? ''
                }
              >
                <SelectTrigger
                  className={STACKED_SELECT_TRIGGER_CLASSNAME}
                  data-testid={autoVehicleFieldTestId(index, 'type')}
                >
                  <SelectValue placeholder='Choose' />
                </SelectTrigger>
                <SelectContent>
                  {AUTO_VEHICLE_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <AutoTransportFieldError
            errors={errors}
            name={`${vehiclePath}.type`}
          />
        </Flex>
      </Flex>

      <Flex
        justify='between'
        align='center'
        gap='md'
        className='w-full min-w-0'
      >
        <Label name={`${vehiclePath}.isInoperable`} className='shrink-0'>
          Condition
        </Label>
        <Controller
          name={`${vehiclePath}.isInoperable`}
          control={control}
          render={({ field: inoperableField }) => (
            <SegmentedToggle
              className='w-[min(100%,10.75rem)] shrink-0'
              value={
                inoperableField.value
                  ? AutoVehicleCondition.Inoperable
                  : AutoVehicleCondition.Operable
              }
              options={CONDITION_OPTIONS}
              onChange={(condition) =>
                inoperableField.onChange(
                  condition === AutoVehicleCondition.Inoperable
                )
              }
            />
          )}
        />
      </Flex>
    </Flex>
  );
}

// Suggestions can differ in casing from the catalog ("toyota" vs "Toyota").
function matchCatalogValue(values: readonly string[], currentValue: string) {
  const lower = currentValue.trim().toLowerCase();
  return values.find((value) => value.toLowerCase() === lower);
}

function toSelectOptions(values: readonly string[], currentValue = '') {
  let options = values;
  if (currentValue && !matchCatalogValue(values, currentValue)) {
    options = [currentValue, ...values];
  }

  return options.map((value) => ({ value, label: value }));
}
